import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQuery } from "./helper";
import {
  PolicySimulationDebugPayload,
  PolicySimulationDebugPayloadVersion,
  PolicySimulationInput,
  PolicySimulationInputDownloadResponse,
  PolicySimulationInputGenerationRequest,
  PolicySimulationRun,
  PolicySimulationRunArtifactDownloadResponse,
  PolicySimulationRunRequest,
} from "../models/policySimulation";

const BASE = "/v1/underwriting/policy-simulations";

export const policySimulationActionSlice = createApi({
  baseQuery,
  reducerPath: "policySimulationApi",
  tagTypes: ["PolicySimulationInput", "PolicySimulationRun"],
  endpoints: (build) => ({
    listInputs: build.query<PolicySimulationInput[], void>({
      query: () => ({ url: `${BASE}/inputs`, method: "GET" }),
      providesTags: ["PolicySimulationInput"],
    }),
    getInput: build.query<PolicySimulationInput, string>({
      query: (inputId) => ({
        url: `${BASE}/inputs/${encodeURIComponent(inputId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, inputId) => [
        { type: "PolicySimulationInput", id: inputId },
      ],
    }),
    createInput: build.mutation<
      PolicySimulationInput,
      PolicySimulationInputGenerationRequest
    >({
      query: (body) => ({
        url: `${BASE}/inputs`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PolicySimulationInput"],
    }),
    getInputDownloadUrl: build.query<
      PolicySimulationInputDownloadResponse,
      string
    >({
      query: (inputId) => ({
        url: `${BASE}/inputs/${encodeURIComponent(inputId)}/download-url`,
        method: "POST",
      }),
    }),
    listRuns: build.query<PolicySimulationRun[], void>({
      query: () => ({ url: `${BASE}/runs`, method: "GET" }),
      providesTags: ["PolicySimulationRun"],
    }),
    getRun: build.query<PolicySimulationRun, string>({
      query: (runId) => ({
        url: `${BASE}/runs/${encodeURIComponent(runId)}`,
        method: "GET",
      }),
      providesTags: (_result, _error, runId) => [
        { type: "PolicySimulationRun", id: runId },
      ],
    }),
    createRun: build.mutation<PolicySimulationRun, PolicySimulationRunRequest>({
      query: (body) => ({
        url: `${BASE}/runs`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PolicySimulationRun"],
    }),
    getRunArtifactDownloadUrl: build.query<
      PolicySimulationRunArtifactDownloadResponse,
      { runId: string; artifact: "jsonl" | "csv" }
    >({
      query: ({ runId, artifact }) => ({
        url: `${BASE}/runs/${encodeURIComponent(runId)}/artifacts/${artifact}/download-url`,
        method: "POST",
      }),
    }),
    getDebugPayload: build.query<
      PolicySimulationDebugPayload,
      {
        runId: string;
        rowNo: number;
        payloadVersion?: PolicySimulationDebugPayloadVersion;
      }
    >({
      query: ({ runId, rowNo, payloadVersion = "effective" }) => ({
        url: `${BASE}/runs/${encodeURIComponent(runId)}/rows/${rowNo}/debug-payload`,
        method: "GET",
        params: { payload_version: payloadVersion },
      }),
    }),
  }),
});

export const {
  useListInputsQuery,
  useGetInputQuery,
  useCreateInputMutation,
  useLazyGetInputDownloadUrlQuery,
  useListRunsQuery,
  useGetRunQuery,
  useCreateRunMutation,
  useLazyGetRunArtifactDownloadUrlQuery,
  useLazyGetDebugPayloadQuery,
} = policySimulationActionSlice;
